import { useMemo } from 'react'

import type { DeliveryDayOption } from '@library/api-customer-engagement-experience'

// Builds the calendar grid from a flat `DeliveryDayOption[]`, using only each entry's id and `date`.
export type CalendarDeliveryDay = Pick<DeliveryDayOption, 'daySlotLeadTimeId' | 'date'> & {
  disabled: boolean
}

const formatDate = (date: Date) => date.toISOString().split('T')[0]

const addNonDeliveryDays = (
  options: DeliveryDayOption[],
  initialDate: string,
  lastDate: string,
): CalendarDeliveryDay[] => {
  const calendarDates: CalendarDeliveryDay[] = []
  const lastDateObj = new Date(lastDate)

  for (let day = new Date(initialDate); day <= lastDateObj; day.setUTCDate(day.getUTCDate() + 1)) {
    const date = formatDate(day)
    const foundOption = options.find((option) => option.date === date)

    if (foundOption) {
      calendarDates.push({
        daySlotLeadTimeId: foundOption.daySlotLeadTimeId,
        date: foundOption.date,
        disabled: false,
      })
    } else {
      calendarDates.push({ daySlotLeadTimeId: '', date, disabled: true })
    }
  }

  return calendarDates
}

export const useDeliveryDayCalendar = (options: DeliveryDayOption[]) => {
  const getCalendarWeekDays = () => ['M', 'T', 'W', 'T', 'F', 'S', 'S']

  const initialState = useMemo(
    () => ({
      getCalendarWeekDays,
      getCalendarDays: () => [] as (CalendarDeliveryDay | null)[][],
      getInitialDate: () => '',
      getLastDate: () => '',
    }),
    [],
  )

  if (!options.length) {
    return initialState
  }

  const initialDate = options[0].date
  const lastDate = options[options.length - 1].date
  const calendarDates = addNonDeliveryDays(options, initialDate, lastDate)

  const getWeekDayForInitialSlot = () => {
    const weekDay = new Date(initialDate).getDay()

    return weekDay === 0 ? 6 : weekDay - 1
  }

  const getCalendarDays = () => {
    const addEmptyDays: (CalendarDeliveryDay | null)[] = new Array(getWeekDayForInitialSlot()).fill(
      null,
    )
    const addDaysFromSlots = addEmptyDays.concat(calendarDates)

    return addDaysFromSlots.reduce((prev: (CalendarDeliveryDay | null)[][], current, index) => {
      if (index % getCalendarWeekDays().length === 0) {
        prev.push([])
      }

      prev[prev.length - 1].push(current)

      return prev
    }, [])
  }

  return {
    getCalendarWeekDays,
    getCalendarDays,
    getInitialDate: () => initialDate,
    getLastDate: () => lastDate,
  }
}
